import { useState } from "react";
import { toast } from "sonner";
import { LocateFixed, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Field, GhostButton } from "@/components/atlas/Fields";
import { api } from "@/lib/api";
import { useLang, errorText } from "@/lib/i18n";

const num = (v) => (v === "" || v === null || Number.isNaN(Number(v)) ? null : Number(v));

const Point = ({ form, setForm, addrKey, la, ln, label, editableAddress, testid }) => {
  const { t } = useLang();
  const [choices, setChoices] = useState([]);
  const pick = (g) => {
    setForm((p) => ({ ...p, [la]: g.lat, [ln]: g.lng }));
    toast.success(`${t("t.located")} : ${g.label}`);
  };
  const locate = async () => {
    try {
      const r = await api.post("/geocode", { address: form[addrKey] });
      pick(r.data);
      setChoices(r.data.results?.length > 1 ? r.data.results : []);
    } catch (e) {
      setChoices([]);
      toast.error(errorText(t, e));
    }
  };
  const has = form[la] !== null && form[la] !== undefined && form[ln] !== null && form[ln] !== undefined;
  return (
    <div className="space-y-3">
      {editableAddress ? (
        <Field label={label}>
          <Input data-testid={`${testid}-address`} value={form[addrKey]} onChange={(e) => setForm((p) => ({ ...p, [addrKey]: e.target.value }))} className="h-10 rounded-none" />
        </Field>
      ) : (
        <div><div className="eyebrow mb-1.5">{label}</div><p className="text-sm text-neutral-600 min-h-[20px]">{form[addrKey] || "—"}</p></div>
      )}
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <Field label={t("t.lat")}><Input data-testid={`${testid}-lat`} inputMode="decimal" value={form[la] ?? ""} onChange={(e) => setForm((p) => ({ ...p, [la]: num(e.target.value) }))} className="h-10 rounded-none mono" /></Field>
        <Field label={t("t.lng")}><Input data-testid={`${testid}-lng`} inputMode="decimal" value={form[ln] ?? ""} onChange={(e) => setForm((p) => ({ ...p, [ln]: num(e.target.value) }))} className="h-10 rounded-none mono" /></Field>
        <GhostButton type="button" data-testid={`${testid}-locate`} disabled={!form[addrKey]} onClick={locate}><LocateFixed className="w-4 h-4" />{t("t.locate")}</GhostButton>
      </div>
      {choices.length > 0 && (
        <div data-testid={`${testid}-choices`} className="space-y-1">
          <div className="eyebrow">{t("t.pickResult")}</div>
          {choices.map((g, i) => (
            <button key={i} type="button" data-testid={`${testid}-choice-${i}`} onClick={() => { pick(g); setChoices([]); }}
              className={`block w-full text-left text-xs px-2 py-1.5 border transition-colors hover:border-[#D9B872] ${g.lat === form[la] && g.lng === form[ln] ? "border-[#D9B872]" : "border-neutral-700/40"}`}>
              {g.label}
            </button>
          ))}
        </div>
      )}
      {has ? (
        <a href={`https://www.google.com/maps/search/?api=1&query=${form[la]},${form[ln]}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#D9B872] underline underline-offset-4"><MapPin className="w-3 h-3" />{t("t.mapCheck")}</a>
      ) : form[addrKey] ? null : null}
    </div>
  );
};

export const GeoFields = ({ form, setForm }) => {
  const { t } = useLang();
  const missing = form.geo_lat == null && form.geo2_lat == null && !form.address && !form.address2;
  return (
    <div data-testid="geo-fields">
      <p className="text-xs text-neutral-500 mb-5">{t("t.radiusHint")}</p>
      <div className="grid md:grid-cols-2 gap-8">
        <Point form={form} setForm={setForm} addrKey="address" la="geo_lat" ln="geo_lng" label={t("f.address")} testid="geo1" />
        <Point form={form} setForm={setForm} addrKey="address2" la="geo2_lat" ln="geo2_lng" label={t("t.address2")} editableAddress testid="geo2" />
      </div>
      {missing && <p className="text-xs text-red-600 mt-4">{t("t.geoMissing")}</p>}
    </div>
  );
};
